/**
 * useJobTarget
 *
 * Owns everything related to the "target job offer" in the editor:
 * - raw input state (paste / URL / title + input mode)
 * - parsed structured offer
 * - derived values (effective job description, canParseJobWithAi, etc.)
 * - localStorage persistence keyed on the active template
 * - fetch-url + parse-with-AI actions
 *
 * Intentionally has no UI-side effects besides toast notifications in the fetch flow
 * (kept to stay behavior-identical with the previous inline App.tsx code).
 */

import { useCallback, useEffect, useState } from 'react';
import { earnPoints } from '../lib/credits-store';
import type { ParsedJobOffer } from '../types';
import {
  parseJobPage,
  parsedOfferToAnalysisText,
  isBlockedOrChallengePage,
} from '../lib/job-url-scraper';
import { parseJsonResponse, postJsonWithTimeout } from '../lib/fetch-utils';
import {
  safeLocalStorageGet,
  safeLocalStorageGetJson,
  safeLocalStorageSet,
  safeLocalStorageSetJson,
  warnOnQuotaOnce,
} from '../lib/safe-storage';
import { toast } from '../lib/toast';

export type JobInputMode = 'paste' | 'url' | 'title';

export interface UseJobTargetReturn {
  // raw state
  jobDescription: string;
  jobUrl: string;
  jobTitle: string;
  jobInputMode: JobInputMode;
  parsedJobOffer: ParsedJobOffer | null;

  // loading flags
  isFetchingUrl: boolean;
  isParsingJobWithAi: boolean;

  // derived values
  rawJobTextForAi: string;
  canParseJobWithAi: boolean;
  effectiveJobDescription: string;
  hasFullAnnouncement: boolean;

  // setters (raw)
  setJobDescription: (v: string) => void;
  setJobUrl: (v: string) => void;
  setJobTitle: (v: string) => void;
  setJobInputMode: (mode: JobInputMode) => void;
  setParsedJobOffer: (offer: ParsedJobOffer | null) => void;

  // actions
  fetchUrl: () => Promise<void>;
  parseJobWithAi: () => Promise<void>;
}

function readLs(key: string, fallback = ''): string {
  return safeLocalStorageGet(key, fallback);
}

function readLsJson<T>(key: string): T | null {
  return safeLocalStorageGetJson<T>(key);
}

export function useJobTarget(activeTemplate: string): UseJobTargetReturn {
  // Initial state is rehydrated from the template active at mount time (matches
  // the previous App.tsx behavior, which also didn't re-hydrate on template switch).
  const initialTemplate =
    readLs('cv_active_template', 'classique') || activeTemplate || 'classique';

  const [jobDescription, setJobDescription] = useState<string>(() =>
    readLs(`cv_job_description_${initialTemplate}`, ''),
  );
  const [jobInputMode, setJobInputMode] = useState<JobInputMode>(() => {
    const v = readLs(`cv_job_input_mode_${initialTemplate}`, 'paste');
    return v === 'url' || v === 'title' || v === 'paste' ? v : 'paste';
  });
  const [jobUrl, setJobUrl] = useState<string>(() =>
    readLs(`cv_job_url_${initialTemplate}`, ''),
  );
  const [jobTitle, setJobTitle] = useState<string>(() =>
    readLs(`cv_job_title_${initialTemplate}`, ''),
  );
  const [parsedJobOffer, setParsedJobOffer] = useState<ParsedJobOffer | null>(() =>
    readLsJson<ParsedJobOffer>(`cv_parsed_job_offer_${initialTemplate}`),
  );

  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [isParsingJobWithAi, setIsParsingJobWithAi] = useState(false);

  // ---- persistence (keyed on activeTemplate) ----
  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_job_description_${activeTemplate}`, jobDescription));
  }, [jobDescription, activeTemplate]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_job_url_${activeTemplate}`, jobUrl));
  }, [jobUrl, activeTemplate]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_job_title_${activeTemplate}`, jobTitle));
  }, [jobTitle, activeTemplate]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet(`cv_job_input_mode_${activeTemplate}`, jobInputMode));
  }, [jobInputMode, activeTemplate]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSetJson(`cv_parsed_job_offer_${activeTemplate}`, parsedJobOffer));
  }, [parsedJobOffer, activeTemplate]);

  // ---- derived values ----
  const fullDescription = parsedJobOffer?.description?.trim();
  const hasFullAnnouncement =
    typeof fullDescription === 'string' && fullDescription.length >= 50;

  const effectiveJobDescription: string = hasFullAnnouncement
    ? (fullDescription as string)
    : parsedJobOffer
      ? parsedOfferToAnalysisText(parsedJobOffer)
      : jobInputMode === 'paste'
        ? jobDescription
        : jobInputMode === 'title'
          ? jobTitle
          : jobDescription;

  const rawJobTextForAi = (parsedJobOffer?.description ?? jobDescription).trim();
  const canParseJobWithAi = rawJobTextForAi.length >= 80;

  // ---- actions ----
  const fetchUrl = useCallback(async () => {
    if (!jobUrl.trim()) return;

    setIsFetchingUrl(true);
    try {
      let html = '';

      // 1) Prefer server-side fetch (no CORS, works for Indeed)
      try {
        const apiRes = await fetch('/api/job/fetch-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: jobUrl.trim() }),
          signal: AbortSignal.timeout(12000),
        });
        if (apiRes.ok) {
          const data = await parseJsonResponse<{ html?: string }>(apiRes);
          if (typeof data?.html === 'string') html = data.html;
        }
      } catch {
        // API unreachable (e.g. static deploy); try CORS proxies below
      }

      // 2) Fallback: CORS proxies (when not using our server)
      if (!html) {
        const corsProxies = [
          `https://api.allorigins.win/raw?url=${encodeURIComponent(jobUrl)}`,
          `https://corsproxy.io/?${encodeURIComponent(jobUrl)}`,
        ];
        for (const proxyUrl of corsProxies) {
          try {
            const response = await fetch(proxyUrl, { signal: AbortSignal.timeout(10000) });
            if (response.ok) {
              html = await response.text();
              break;
            }
          } catch {
            continue;
          }
        }
      }

      if (html) {
        if (isBlockedOrChallengePage(html)) {
          toast.error(
            "Ce site bloque l'accès automatique (Cloudflare). Utilisez le bouton « Ouvrir » pour ouvrir l'annonce dans un nouvel onglet, copiez tout le texte (Ctrl+A, Ctrl+C), puis passez en « Coller » et collez ici.",
          );
          return;
        }
        const result = parseJobPage(html, jobUrl.trim());
        if (result.ok && result.text.length > 100) {
          setParsedJobOffer(result.parsed);
          setJobDescription(result.text);
          setJobInputMode('paste');
          // Gamification: earn 1pt for browsing a job offer via URL
          earnPoints('browseOffer');
        } else {
          toast.error(
            "Impossible d'extraire le contenu de l'annonce depuis cette URL. Essayez de copier-coller l'annonce manuellement.",
          );
        }
      } else {
        toast.error(
          "Impossible d'accéder à l'URL. Utilisez l'app en local (bun run dev) pour les liens Indeed, ou copiez l'annonce manuellement.",
        );
      }
    } catch (error) {
      console.error('URL fetch error:', error);
      toast.error("Erreur lors de la récupération de l'URL. Collez l'annonce manuellement.");
    } finally {
      setIsFetchingUrl(false);
    }
  }, [jobUrl]);

  const parseJobWithAi = useCallback(async () => {
    if (!canParseJobWithAi) return;
    setIsParsingJobWithAi(true);
    try {
      const { res, json } = await postJsonWithTimeout<{ error?: string; data?: unknown }>(
        '/api/ai',
        { route: 'parse', jobDescription: rawJobTextForAi },
      );
      if (!res.ok) throw new Error(json.error || 'Erreur parsing IA annonce');
      const parsed = json.data as ParsedJobOffer | undefined;
      if (parsed && typeof parsed === 'object') {
        setParsedJobOffer(parsed);
        setJobDescription(parsedOfferToAnalysisText(parsed));
      }
    } catch (e) {
      console.error('[Parse job IA]', e);
      const msg = e instanceof Error ? e.message : 'Erreur parsing IA annonce';
      toast.error(msg);
    } finally {
      setIsParsingJobWithAi(false);
    }
  }, [canParseJobWithAi, rawJobTextForAi]);

  return {
    jobDescription,
    jobUrl,
    jobTitle,
    jobInputMode,
    parsedJobOffer,
    isFetchingUrl,
    isParsingJobWithAi,
    rawJobTextForAi,
    canParseJobWithAi,
    effectiveJobDescription,
    hasFullAnnouncement,
    setJobDescription,
    setJobUrl,
    setJobTitle,
    setJobInputMode,
    setParsedJobOffer,
    fetchUrl,
    parseJobWithAi,
  };
}
